<template>
  <div class="nx-list-page">
    <avue-crud
      ref="crud"
      v-model="form"
      :option="option"
      :table-loading="loading"
      :data="data"
      :page.sync="page"
      :search.sync="query"
      :defaults.sync="defaults"
      @search-change="searchChange"
      @search-reset="searchReset"
      @refresh-change="refreshChange"
      @current-change="currentChange"
      @size-change="sizeChange"
      @on-load="onLoad"
    >
      <template slot="menuLeft">
        <el-button
          type="primary"
          size="small"
          plain
          icon="el-icon-download"
          :loading="exportLoading"
          @click="handleExport"
        >导出PDF</el-button>
      </template>
    </avue-crud>
  </div>
</template>

<script>
import crudMixins from "@/mixins/crud-mixins";
import { searchTimeConfig } from "@/basic-common/util/common";
import { getSettingDate } from "@/util/date";
import { exportPdf, getList } from "./api";

const RECEIVER_COMPANY = "宁夏物流园煤炭储备(中卫)有限公司";
const EXPORT_MAX_ROWS = 10000;

function textValue(val) {
  if (val === null || val === undefined || val === '') return '-'
  return String(val)
}

function displayName(name, no) {
  return textValue(name || no)
}

function fixedZero() {
  return '0.00'
}

function formatDecimal(val) {
  if (val === null || val === undefined || val === '') return '-'
  const num = Number(val)
  if (!Number.isFinite(num)) return String(val)
  return num.toFixed(2)
}

function parseDateTimeParts(val) {
  if (val == null || val === '') return null
  if (val instanceof Date && !Number.isNaN(val.getTime())) {
    const y = val.getFullYear()
    const mo = String(val.getMonth() + 1).padStart(2, '0')
    const da = String(val.getDate()).padStart(2, '0')
    const h = String(val.getHours()).padStart(2, '0')
    const mi = String(val.getMinutes()).padStart(2, '0')
    const s = String(val.getSeconds()).padStart(2, '0')
    return { date: `${y}-${mo}-${da}`, time: `${h}:${mi}:${s}` }
  }
  const str = String(val).trim()
  const m = str.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})/)
  if (m) return { date: m[1], time: m[2] }
  if (/^\d{13}$/.test(str)) {
    const d = new Date(Number(str))
    if (!Number.isNaN(d.getTime())) return parseDateTimeParts(d)
  }
  const d = new Date(str)
  if (!Number.isNaN(d.getTime())) return parseDateTimeParts(d)
  return null
}

function fullDateTimeText(val) {
  const p = parseDateTimeParts(val)
  return p ? `${p.date} ${p.time}` : '-'
}

function downloadPdf(blobData, filename) {
  const blob = blobData instanceof Blob ? blobData : new Blob([blobData], { type: 'application/pdf' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export default {
  mixins: [crudMixins],
  data() {
    return {
      exportLoading: false,
      apiConfig: { getList },
      defaultParams: {
        businessType: "3",
        vehicleStatus: 2,
      },
      option: {
        ...this.$store.state.publicOption,
        addBtn: false,
        editBtn: false,
        delBtn: false,
        viewBtn: false,
        menu: false,
        column: [
          {
            label: '磅单号',
            prop: 'weightNo',
            search: true,
            width: 150,
          },
          {
            label: '发货企业',
            prop: 'custNo',
            type: 'select',
            dicUrl: '/api/produce/blade-dept/select/distinct/select',
            search: true,
            props: {
              label: 'deptName',
              value: 'id'
            },
            formatter: (row) => displayName(row.custName, row.custNo),
            width: 180,
          },
          {
            label: '收货企业',
            prop: 'receiverCompany',
            formatter: () => RECEIVER_COMPANY,
            width: 260,
          },
          {
            label: '煤种',
            prop: 'cargoNo',
            type: 'select',
            remote: true,
            search: true,
            dicUrl: '/api/produce/vardict/select/distinct/pycode?varName={{key}}',
            props: {
              label: 'varName',
              value: 'varNo'
            },
            formatter: (row) => displayName(row.cargoName, row.cargoNo),
            width: 150,
          },
          {
            label: '车牌号',
            prop: 'trainNumber',
            search: true,
            width: 130,
          },
          {
            label: '司机',
            prop: 'driverName',
            width: 120,
          },
          {
            label: '司机手机号',
            prop: 'phoneNo',
            width: 140,
          },
          {
            label: '起点',
            prop: 'startPlace',
            formatter: () => '',
            width: 100,
          },
          {
            label: '终点',
            prop: 'stockName',
            width: 160,
          },
          {
            label: '毛重',
            prop: 'grossWeight',
            formatter: (row) => formatDecimal(row.grossWeight),
            width: 100,
          },
          {
            label: '皮重',
            prop: 'tareWeight',
            formatter: (row) => formatDecimal(row.tareWeight),
            width: 100,
          },
          {
            label: '净重',
            prop: 'netWeight',
            formatter: (row) => formatDecimal(row.netWeight),
            width: 100,
          },
          {
            label: '扣减重量',
            prop: 'deductWeight',
            formatter: fixedZero,
            width: 110,
          },
          {
            label: '矿发净重',
            prop: 'mineNetWeight',
            formatter: fixedZero,
            width: 110,
          },
          {
            label: '扣重',
            prop: 'lossWeight',
            formatter: fixedZero,
            width: 100,
          },
          {
            label: '一次时间',
            prop: 'heavyTime',
            formatter: (row) => fullDateTimeText(row.heavyTime),
            width: 165,
          },
          {
            label: '二次时间',
            prop: 'emptyTime',
            formatter: (row) => fullDateTimeText(row.emptyTime),
            width: 165,
          },
          {
            ...searchTimeConfig,
            label: "进门时间",
            paramsKey: ["startTime", "endTime"],
            searchValue: [getSettingDate('daySLast30'), getSettingDate('dayE')],
          }
        ]
      },
    }
  },
  methods: {
    async handleExport() {
      const total = this.page.total || 0
      if (total < 1) {
        this.$message.warning('无数据可导出')
        return
      }
      if (total > EXPORT_MAX_ROWS) {
        this.$message.warning(`当前最多导出 ${EXPORT_MAX_ROWS} 条，请缩小筛选范围后重试`)
        return
      }
      this.exportLoading = true
      try {
        const params = this.formatParams(Object.assign({}, this.defaultParams, this.query))
        const res = await exportPdf(params)
        downloadPdf(res.data || res, `入库电子磅单_${Date.now()}.pdf`)
        this.$message.success('导出成功')
      } catch (e) {
        console.warn(e)
        this.$message.error((e && e.message) || '导出失败')
      } finally {
        this.exportLoading = false
      }
    },
  }
}
</script>

<style scoped lang="scss">
</style>
